import { multiplesOf } from "./array-helper";
import md5 from "md5";

export class NoSlidesAvailableToDuplicateError extends Error {
    constructor(message = 'No slides available to duplicate') {
        super(message);

        this.name = 'NoSlidesAvailableToDuplicateError';
    }
}

export class TooManyDuplicateSlideAttemptsError extends Error {
    constructor(message = 'Too many duplicate slide attempts have been made, we are potentially in an infinite loop') {
        super(message);

        this.name = 'TooManyDuplicateSlideAttemptsError';
    }
}

/**
 * Determines if the amount of slides meets the constraints of Swiper to allow for looping
 *
 * @param {import("swiper/types/swiper-class.d.ts").Swiper} swiper
 * @returns boolean
 */
export const isSwiperValidForLooping = (swiper) => {
    const slideCount = swiper.el.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
    const slideSettingsToCheck = {
        slidesPerView: [],
        slidesPerGroup: [],
    };

    if (! Number.isNaN(swiper.params.slidesPerView)) {
        slideSettingsToCheck.slidesPerView.push(swiper.originalParams.slidesPerView);
    }

    if (! Number.isNaN(swiper.params.slidesPerGroup)) {
        slideSettingsToCheck.slidesPerGroup.push(swiper.originalParams.slidesPerGroup);
    }

    if (typeof swiper.params.breakpoints === 'object') {
        for (const breakpointKey in swiper.params.breakpoints) {
            const breakpoint = swiper.params.breakpoints[breakpointKey];

            if (! Number.isNaN(breakpoint.slidesPerView)) {
                slideSettingsToCheck.slidesPerView.push(breakpoint.slidesPerView);
            }

            if (! Number.isNaN(breakpoint.slidesPerGroup)) {
                slideSettingsToCheck.slidesPerGroup.push(breakpoint.slidesPerGroup);
            }
        }
    }

    for (const slideSettingKey in slideSettingsToCheck) {
        const slideSetting = slideSettingsToCheck[slideSettingKey];

        for (const minimumSlideCount of slideSetting) {
            const allowedSlideCount = multiplesOf(minimumSlideCount, 100);

            if (! allowedSlideCount.includes(slideCount)) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(`[isSwiperValidForLooping] Actual slide count of ${slideCount} is not valid for the given multiple constraint of ${minimumSlideCount}, defined in "${slideSettingKey}"`);
                }

                return false;
            }
        }
    }

    return true;
}

/**
 * Adds a duplicated slide to a given Swiper element
 *
 * @param {import("swiper/types/swiper-class.d.ts").Swiper} swiper
 */
export const addDuplicateSlideToSwiper = (swiper) => {
    const swiperWrapper = swiper.el.querySelector('.swiper-wrapper');
    const slideElements = swiperWrapper.querySelectorAll('.swiper-slide');

    let slides = {};
    let slideUsages = {};

    slideElements.forEach((slideElement) => {
        // innerHTML as hash and outerHTML as slide data is intentional,
        // SwiperJS adds some data attributes, which would generate a different hash,
        // while we need the entire outerHTML element so the slide is also wrapped
        const slideElementHash = md5(slideElement.innerHTML);

        slides[slideElementHash] = slideElement.outerHTML;

        if (! (slideElementHash in slideUsages)) {
            slideUsages[slideElementHash] = 0;
        }

        slideUsages[slideElementHash] += 1;
    });

    let leastUsedSlideHash  = null;
    let leastUsedSlideCount = Infinity;

    for (const [slideElementHash, slideUsage] of Object.entries(slideUsages)) {
        if (slideUsage < leastUsedSlideCount) {
            leastUsedSlideHash  = slideElementHash;
            leastUsedSlideCount = slideUsage;
        }
    }

    if (leastUsedSlideHash === null) {
        // There are no slides at all, can't make this situation any better :(

        throw new NoSlidesAvailableToDuplicateError();
    }

    swiperWrapper.insertAdjacentHTML('beforeend', slides[leastUsedSlideHash]);

    // Update the swiper so it picks up the new slide
    swiper.update()
}

/**
 * Checks if a swiper is ready for looping and if not, it will add duplicated slides until it is valid
 * Call this function in the "beforeInit" event listener of your Swiper object
 *
 * @param {import("swiper/types/swiper-class.d.ts").Swiper} swiper
 */
export const ensureSwiperIsReadyForLooping = (swiper) => {
    const maximumAttempts = 50;
    let attemptCounter = 1;

    while (! isSwiperValidForLooping(swiper)) {
        if (attemptCounter > maximumAttempts) {
            throw new TooManyDuplicateSlideAttemptsError();
        }

        attemptCounter++;

        console.warn('Swiper instance is not ready for looping, duplicating slides to prevent issues!', swiper);

        try {
            addDuplicateSlideToSwiper(swiper);
        } catch (e) {
            if (e instanceof NoSlidesAvailableToDuplicateError) {
                console.warn('Unable to duplicate slides to prevent issues, as there are no slides available', swiper);

                break;
            }

            throw e;
        }
    }
}
